import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, X, Calendar, Clock, Folder, Star } from 'lucide-react';
import { api } from '../lib/api';
import { pushToast } from '../lib/toast';
import { toasts as copy } from '@shared/copy';
import { fromInputDate, toInputDate } from '../lib/dates';

const STATUSES = ['inbox', 'next', 'waiting', 'scheduled', 'someday', 'done'] as const;
const ENERGIES = ['', 'low', 'medium', 'high'] as const;

export function TaskEditor({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const task = useQuery({ queryKey: ['tasks', taskId], queryFn: () => api.getTask(taskId) });
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.listProjects });

  const [draft, setDraft] = useState<any | null>(null);
  useEffect(() => {
    if (task.data && !draft) setDraft(task.data);
  }, [task.data, draft]);

  const save = useMutation({
    mutationFn: async (patch: any) => api.updateTask(taskId, patch),
    onSuccess: () => {
      pushToast(copy.taskUpdated);
      qc.invalidateQueries({ queryKey: ['view'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const del = useMutation({
    mutationFn: () => api.deleteTask(taskId),
    onSuccess: () => {
      pushToast(copy.taskDeleted);
      qc.invalidateQueries({ queryKey: ['view'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      onClose();
    },
  });

  function patch(p: any) {
    setDraft((d: any) => ({ ...d, ...p }));
    save.mutate(p);
  }

  if (!draft) {
    return (
      <Drawer onClose={onClose}>
        <div className="p-6 text-zinc-500 text-sm">Loading…</div>
      </Drawer>
    );
  }

  return (
    <Drawer onClose={onClose}>
      <div className="p-4 md:p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => patch({ focus: !draft.focus })} className="text-amber-400">
            <Star className={`w-5 h-5 ${draft.focus ? 'fill-amber-400' : 'opacity-50'}`} />
          </button>
          <div className="flex gap-1">
            <button
              onClick={() => {
                if (confirm('Delete this task? Cannot be undone.')) del.mutate();
              }}
              className="p-2 text-red-400 hover:bg-zinc-800 rounded-lg"
              aria-label="Delete task"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 text-zinc-400 hover:bg-zinc-800 rounded-lg" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <input
          className="bg-transparent text-xl font-medium outline-none mb-2"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          onBlur={() => {
            if (draft.title.trim() && draft.title !== task.data?.title) save.mutate({ title: draft.title.trim() });
          }}
          placeholder="Title"
        />

        <textarea
          className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 outline-none text-sm min-h-[120px] mb-3 focus:border-amber-500"
          placeholder="Notes — markdown is fine. Or just rambles."
          value={draft.notes ?? ''}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          onBlur={() => save.mutate({ notes: draft.notes ?? null })}
        />

        <div className="grid grid-cols-2 gap-2 text-sm">
          <Field label="Status">
            <select
              className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 w-full"
              value={draft.status}
              onChange={(e) => patch({ status: e.target.value })}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Energy">
            <select
              className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 w-full"
              value={draft.energy ?? ''}
              onChange={(e) => patch({ energy: e.target.value || null })}
            >
              {ENERGIES.map((e) => <option key={e} value={e}>{e || '—'}</option>)}
            </select>
          </Field>
          <Field label="Due" icon={<Calendar className="w-3.5 h-3.5" />}>
            <input
              type="date"
              className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 w-full"
              value={toInputDate(draft.due_at)}
              onChange={(e) => patch({ dueAt: fromInputDate(e.target.value) })}
            />
          </Field>
          <Field label="Defer until" icon={<Clock className="w-3.5 h-3.5" />}>
            <input
              type="date"
              className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 w-full"
              value={toInputDate(draft.defer_at)}
              onChange={(e) => patch({ deferAt: fromInputDate(e.target.value) })}
            />
          </Field>
          <Field label="Project" icon={<Folder className="w-3.5 h-3.5" />}>
            <select
              className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 w-full"
              value={draft.project_id ?? draft.projectId ?? ''}
              onChange={(e) => patch({ projectId: e.target.value || null })}
            >
              <option value="">—</option>
              {(projects.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Estimate (min)">
            <input
              type="number"
              min={1}
              className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 w-full"
              value={draft.estimate_minutes ?? draft.estimateMinutes ?? ''}
              onChange={(e) => setDraft({ ...draft, estimate_minutes: e.target.value ? Number(e.target.value) : null })}
              onBlur={() => save.mutate({ estimateMinutes: draft.estimate_minutes ?? null })}
            />
          </Field>
          {draft.status === 'waiting' && (
            <Field label="Waiting on">
              <input
                className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 w-full"
                value={draft.waiting_for ?? ''}
                onChange={(e) => setDraft({ ...draft, waiting_for: e.target.value })}
                onBlur={() => save.mutate({ waitingFor: draft.waiting_for ?? null })}
                placeholder="Whose problem is this?"
              />
            </Field>
          )}
        </div>

        <div className="mt-4 text-xs text-zinc-500">
          Created {new Date(draft.created_at).toLocaleString()}. Updated {new Date(draft.updated_at).toLocaleString()}.
        </div>
      </div>
    </Drawer>
  );
}

function Field({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-500 flex items-center gap-1 mb-0.5">{icon}{label}</span>
      {children}
    </label>
  );
}

function Drawer({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-stretch md:justify-end" onClick={onClose}>
      <div
        className="bg-zinc-900 border-l border-zinc-800 w-full md:w-[28rem] md:min-h-full max-h-[90dvh] md:max-h-none overflow-y-auto rounded-t-2xl md:rounded-none animate-pop overscroll-contain"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="md:hidden mx-auto h-1 w-10 rounded-full bg-zinc-700 my-2" aria-hidden />
        {children}
      </div>
    </div>
  );
}
